import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LoaderFunctionArgs, redirect } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Outlet, useLoaderData, useLocation, useNavigation } from "@remix-run/react";
import * as Sentry from "@sentry/remix";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { sendMessage } from "~/hooks/useSendMessage";
import { StateManagerProvider, useStateManager } from "~/modules/StateManager.hooks";
import { redisCache } from "~/services/redis.server";

export async function loader(args: LoaderFunctionArgs) {
  const state = await redisCache.getSession(args.context.sessionId);

  if (!state) throw redirect("../plugin");

  return json({ __STRINGIFIED_STATE__: state });
}

export default function SessionIdLayout() {
  const data = useLoaderData<typeof loader>();

  return (
    <StateManagerProvider stringifiedState={data.__STRINGIFIED_STATE__}>
      <SessionLayout />
    </StateManagerProvider>
  );
}

function SessionLayout() {
  const state = useStateManager();
  const navigation = useNavigation();
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  const locale = state.state.config.language;
  useChangeLanguage(locale);

  const warning = state.state.warning;
  useEffect(() => {
    if (warning) {
      console.warn(warning);

      // TODO differentiate between different types. Currently only warnings are thrown/logged
      // when there is an invalid flow caught.
      sendMessage({ type: "INVALID_FLOW", payload: warning });
    }
  }, [warning]);

  useEffect(() => {
    locale && Sentry.setTag("pexip.locale", locale);
  }, [locale]);

  useEffect(() => {
    sendMessage({ type: "STATE_UPDATE", payload: state.state });
  }, [state.state]);

  const offset = state.state.config.application.scroll;
  useEffect(() => {
    if (navigation.state !== "idle") return;
    if (offset === "disabled") return;

    if (pathname === previousPathname.current) return;

    previousPathname.current = pathname;
    if (window.parentIFrame) {
      window.parentIFrame.scrollToOffset(0, offset);
    } else {
      window.scrollTo({ top: offset });
    }
  }, [navigation.state, offset, pathname]);

  return <Outlet />;
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}

/**
 * Workaround for https://github.com/sergiodxa/remix-i18next/issues/107
 */
function useChangeLanguage(locale?: string) {
  let { i18n } = useTranslation();
  useEffect(() => {
    locale && i18n.changeLanguage(locale);
  }, [locale, i18n]);
}
